import React, { FC, Fragment } from 'react';

import { Icon, ViewProps } from '@rbilabs/universal-components';
import { isEmpty } from 'lodash-es';

import { A11yWrapper } from 'components/a11y-wrapper';
import Picture from 'components/picture';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { parseIncentiveData } from 'pages/loyalty/loyalty-incentives-components/incentive-card/parse-incentive-data';
import { useLoyaltyWalmart } from 'pages/loyalty/loyalty-partners/walmart/use-loyalty-walmart';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import {
  IIncentiveEvaluationResult,
  IncentiveEvaluationErrorCodes,
} from 'state/loyalty/hooks/types';
import { getEvaluationResultMessage } from 'state/loyalty/hooks/utils/helpers';
import {
  getWalmartSeasonalConfigOfferIds,
  isAnyWalmartOffer,
} from 'state/loyalty/hooks/utils/walmart';
import { getCmsOffersMapByCmsId } from 'state/loyalty/utils';

import { OfferStatusContainerSkeleton } from './cart-item-offer.skeleton';
import {
  OfferFeedbackAdditionalInfo,
  useFeedbackAdditionalInfo,
} from './hooks/use-feedback-additional-info';
import { IncentiveName, ItemOffer, ItemOfferFeedback, OfferFeedbackMessage } from './styled';
import { ICartItemOfferProps } from './types';

export const experimentalMessages = {
  [IncentiveEvaluationErrorCodes.BELOW_MINIMUM_SPEND]: 'cartV2_incentiveFeedbackSubtotal',
  [IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD]: 'cartV2_incentiveFeedbackPaymentMethod',
  [IncentiveEvaluationErrorCodes.UNSATISFIED_CART_REQUIREMENTS]:
    'cartV2_incentiveFeedbackCartRequirement',
};

export const WalmartBadgeContainer = ({
  marginBottom,
}: {
  marginBottom?: ViewProps['marginBottom'];
}) => {
  const { data: loyaltyWalmartData } = useLoyaltyWalmart();

  return (
    <Picture
      height="$6"
      image={loyaltyWalmartData?.badge?.locale}
      alt={t('walmartOfferBadgeAlt')}
      marginBottom={marginBottom || '$1'}
    />
  );
};

/**
 * OfferStatusContainer component renders a feedback message with an associated icon.
 * It displays a fixed message indicating the offer status and, if applicable, additional
 * feedback messages.
 *
 */
const OfferStatusContainer = React.memo(
  ({
    offerFeedback,
    offerFeedbackAdditionalInfo,
  }: {
    offerFeedback: IIncentiveEvaluationResult[];
    offerFeedbackAdditionalInfo?: OfferFeedbackAdditionalInfo;
  }) => {
    const { cartEntries } = useCart();
    const hasOfferFeedback = !isEmpty(offerFeedback);

    const textSeparator = ' - ';
    const fixedMessage = hasOfferFeedback ? t('offerNotApplied') : t('offerApplied');

    const feedbackMessages = offerFeedback?.map(feedback =>
      getEvaluationResultMessage({
        evaluationResult: feedback,
        incentiveType: LoyaltyPromotionType.OFFER,
        cartEntries,
        experimentalMessages,
        forceOnlyStringMessage: true,
        additionalInfo: offerFeedbackAdditionalInfo,
      })
    );

    return (
      <ItemOfferFeedback>
        <Icon marginRight={1} variant={hasOfferFeedback ? 'errorV2' : 'checkFill'} size="3" />
        <OfferFeedbackMessage>
          {fixedMessage}
          {feedbackMessages?.map((message, index) => (
            <Fragment key={index}>
              {textSeparator}
              {message}
            </Fragment>
          ))}
        </OfferFeedbackMessage>
      </ItemOfferFeedback>
    );
  }
);

export const CartItemOffer: FC<React.PropsWithChildren<ICartItemOfferProps>> = ({
  loyaltyOfferId,
  cmsOfferId,
  isLoading,
  skeletonRenderedRef,
}) => {
  const cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const cmsOffersMap = getCmsOffersMapByCmsId(cmsOffers);
  const selectedOffer = cmsOffersMap[cmsOfferId || ''];
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const loyaltyWalmartUILoading = useAppSelector(selectors.loyalty.selectLoyaltyWalmartUILoading);

  const { data: loyaltyWalmart } = useLoyaltyWalmart();

  const isWalmartOfferSelected =
    isAnyWalmartOffer(selectedOffer || {}) ||
    (cmsOfferId && getWalmartSeasonalConfigOfferIds(loyaltyWalmart).includes(cmsOfferId));

  const { loading: loadingFeedbackAdditionalInfo, additionalInfo } = useFeedbackAdditionalInfo({
    loyaltyOfferId: loyaltyOfferId || '',
  });

  if (selectedOffer && loyaltyOfferId) {
    // To avoid flickering, we set the ref to true once the component is rendered and the offer is loaded.
    if (!isLoading && skeletonRenderedRef && !skeletonRenderedRef.current) {
      skeletonRenderedRef.current = true;
    }

    const offerFeedback = offersFeedbackMap[loyaltyOfferId];
    const incentiveData = parseIncentiveData(selectedOffer);

    return (
      <ItemOffer isLoading={isLoading} $isApplied={!offerFeedback}>
        {(isWalmartOfferSelected || loyaltyWalmartUILoading) && <WalmartBadgeContainer />}
        <A11yWrapper>
          <IncentiveName marginBottom="$1">{incentiveData.name}</IncentiveName>
        </A11yWrapper>
        {isLoading || loadingFeedbackAdditionalInfo ? (
          <OfferStatusContainerSkeleton />
        ) : (
          <OfferStatusContainer
            offerFeedback={offerFeedback}
            offerFeedbackAdditionalInfo={additionalInfo}
          />
        )}
      </ItemOffer>
    );
  }

  return null;
};
